import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Tabs, Select } from 'antd';
import ApplicationService from '../services/axios.service';

const { TabPane } = Tabs;
const service = new ApplicationService();

const SalesTotal = () => {
  const [data, setData] = useState({});
  const [budgetData, setBudgetData] = useState({});
  const [loading, setLoading] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarters, setSelectedQuarters] = useState(['Q1', 'Q2', 'Q3', 'Q4']);
  const [budgetYear, setBudgetYear] = useState(new Date().getFullYear());
  const [budgetQuarters, setBudgetQuarters] = useState(['Q1', 'Q2', 'Q3', 'Q4']);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [budgetEditingCell, setBudgetEditingCell] = useState(null);
  const [budgetEditValue, setBudgetEditValue] = useState('');
  const [budgetHasUnsavedChanges, setBudgetHasUnsavedChanges] = useState(false);
  const [budgetPendingChanges, setBudgetPendingChanges] = useState({});
  const navigate = useNavigate();
  const editValueRef = useRef('');
  const editingCellRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  useEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Get current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  };

  const isCurrentMonth = (monthKey) => {
    const currentMonthYear = getCurrentMonthYear();
    return monthKey === currentMonthYear;
  };

  const fetchSalesTotalData = async () => {
    if (selectedQuarters.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        year: selectedYear,
        quarters: selectedQuarters.join(',')
      };

      const response = await service.getSalesTotalGrouped(payload);
      console.log('Sales Total Data:', response);
      
      if (response.data.success) {
        setData(response.data.data || {});
        toast.success('Data fetched successfully!');
      } else {
        setData({});
        toast.info('No data found');
      }
    } catch (error) {
      console.error('Error fetching sales total:', error);
      toast.error('Failed to fetch sales total data');
      setData({});
    } finally {
      setLoading(false);
    }
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarters(prev => 
      prev.includes(quarter) 
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const handleCellEdit = (customerName, partNumber, month, field, value) => {
    setData(prevData => {
      const newData = { ...prevData };
      const monthRecords = newData[month];
      const recordIndex = monthRecords.findIndex(
        r => r.customer_name === customerName && r.part_number === partNumber
      );
      if (recordIndex > -1) {
        monthRecords[recordIndex] = {
          ...monthRecords[recordIndex],
          [field]: value
        };
      }
      return newData;
    });
  };

  const startEdit = (customerName, partNumber, month, field, currentValue) => {
    const cellData = { customerName, partNumber, month, field };
    
    // Handle both string and number values from API
    let valueToEdit = '';
    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      // Convert to number first, then to string for editing
      const numVal = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;
      valueToEdit = isNaN(numVal) ? '' : String(numVal);
    }
    
    setEditingCell(cellData);
    setEditValue(valueToEdit);
    editingCellRef.current = cellData;
    editValueRef.current = valueToEdit;
  };

  const saveEdit = () => {
    const cell = editingCellRef.current;
    const value = editValueRef.current;
    
    if (!cell) return;

    // Convert to number for saving
    const numericValue = value === '' || value === null ? 0 : parseFloat(value);
    
    if (isNaN(numericValue)) {
      toast.error('Invalid number');
      cancelEdit();
      return;
    }

    const key = `${cell.customerName}_${cell.partNumber}_${cell.month}_${cell.field}`;
    
    setPendingChanges(prev => ({
      ...prev,
      [key]: {
        customerName: cell.customerName,
        partNumber: cell.partNumber,
        month: cell.month,
        field: cell.field,
        value: numericValue
      }
    }));

    setData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      const monthRecords = newData[cell.month];
      
      if (monthRecords) {
        const recordIndex = monthRecords.findIndex(
          r => r.customer_name === cell.customerName && r.part_number === cell.partNumber
        );
        
        if (recordIndex > -1) {
          // Store as number to match API format
          monthRecords[recordIndex][cell.field] = numericValue;
        }
      }
      
      return newData;
    });

    setHasUnsavedChanges(true);
    cancelEdit();
  };

  const handleSaveAll = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      // Group changes by record
      const recordUpdates = {};
      
      Object.values(pendingChanges).forEach(change => {
        const recordKey = `${change.customerName}_${change.partNumber}_${change.month}`;
        if (!recordUpdates[recordKey]) {
          recordUpdates[recordKey] = {
            customerName: change.customerName,
            partNumber: change.partNumber,
            month: change.month,
            fields: {}
          };
        }
        recordUpdates[recordKey].fields[change.field] = change.value;
      });

      // Save each record
      for (const update of Object.values(recordUpdates)) {
        const monthRecords = data[update.month];
        const record = monthRecords.find(
          r => r.customer_name === update.customerName && r.part_number === update.partNumber
        );

        if (record) {
          const payload = {
            id: record.id,
            customer_name: update.customerName,
            part_number: update.partNumber,
            month: update.month,
            ...update.fields
          };

          await service.updateSalesTotal(payload);
        }
      }

      toast.success("All changes saved successfully!");
      setHasUnsavedChanges(false);
      setPendingChanges({});
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Error saving changes");
    }
  };

  const handleCancelAll = () => {
    setPendingChanges({});
    setHasUnsavedChanges(false);
    fetchSalesTotalData(); // Reload original data
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
    editingCellRef.current = null;
    editValueRef.current = '';
  };

  const isEditing = (customerName, partNumber, month, field) => {
    return editingCell?.customerName === customerName &&
           editingCell?.partNumber === partNumber &&
           editingCell?.month === month &&
           editingCell?.field === field;
  };

  const renderDataTable = (forecastData) => {
    const groupedRecords = {};
    
    Object.entries(forecastData).forEach(([month, records]) => {
      records.forEach(record => {
        const key = `${record.customer_name}_${record.part_number}`;
        if (!groupedRecords[key]) {
          groupedRecords[key] = {
            customer_name: record.customer_name,
            part_number: record.part_number,
            model_name: record.model_name,
            months: {}
          };
        }
        groupedRecords[key].months[month] = record;
      });
    });

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthNum = currentDate.getMonth() + 1;
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthName = monthNames[currentMonthNum - 1];
    const currentMonth = `${currentMonthName} ${currentYear}`;

    const isCurrentMonth = (month) => {
      return month === currentMonth;
    };
    
    const isPastMonth = (month) => {
      const [monthName, yearStr] = month.split(' ');
      const monthYear = new Date(parseInt(yearStr), monthNames.indexOf(monthName));
      const current = new Date(currentYear, currentMonthNum - 1);
      return monthYear < current;
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '12px' }}>
            <thead>
              <tr className="bg-yellow-300" style={{ position: 'sticky', top: 0, zIndex: 25, height: '41px' }}>
                <th className="border border-gray-300 px-2 py-1 text-center sticky left-0 bg-yellow-300 z-30" rowSpan="2" style={{ minWidth: '100px', maxWidth: '100px', width: '100px', padding: '4px 8px', lineHeight: '1.2' }}>
                  Customer Name
                </th>
                <th className="border border-gray-300 px-2 py-1 text-center sticky left-[100px] bg-yellow-300 z-30" rowSpan="2" style={{ minWidth: '120px', maxWidth: '120px', width: '120px', padding: '4px 8px', lineHeight: '1.2' }}>
                  Part Number
                </th>
                <th className="border border-gray-300 px-2 py-1 text-center sticky left-[220px] bg-yellow-300 z-30" rowSpan="2" style={{ minWidth: '200px', maxWidth: '200px', width: '200px', padding: '4px 8px', lineHeight: '1.2' }}>
                  Model Name
                </th>
                {Object.keys(forecastData).map((month, idx) => {
                  const isCurrent = isCurrentMonth(month);
                  const isPast = isPastMonth(month);
                  const showExtendedColumns = isCurrent || !isPast;
                  const colSpan = showExtendedColumns ? 14 : 3;
                  
                  return (
                    <th key={idx} className="border border-gray-300 px-2 py-1 text-center bg-yellow-300" colSpan={colSpan} style={{ padding: '4px 8px', lineHeight: '1.2' }}>
                      {month}
                    </th>
                  );
                })}
              </tr>
              <tr className="bg-blue-100" style={{ position: 'sticky', top: '41px', zIndex: 20, height: '41px' }}>
                {Object.keys(forecastData).map((month, idx) => {
                  const isCurrent = isCurrentMonth(month);
                  const isPast = isPastMonth(month);
                  const showExtendedColumns = isCurrent || !isPast;

                  return (
                    <React.Fragment key={idx}>
                      {/* Basic columns always shown */}
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-blue-100" style={{ minWidth: '80px' }}>Unit Price</th>
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-blue-100" style={{ minWidth: '60px' }}>QTY</th>
                      <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-blue-100" style={{ minWidth: '80px' }}>AMT</th>
                      
                      {showExtendedColumns && (
                        <>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>Firm PO Qty</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>Firm PO Amt</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '90px' }}>Firm PO Risk Qty</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '90px' }}>Firm PO Risk Amt</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>FC VMI Low Risk Qty</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>FC VMI Low Risk Amt</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>FC VMI High Risk Qty</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '80px' }}>FC VMI High Risk Amt</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '100px' }}>Markup Qty Historical</th>
                          <th className="border border-gray-300 px-2 py-1 text-center text-xs bg-red-100" style={{ minWidth: '100px' }}>Markup Amt Historical</th>
                          
                          {/* Delta - LAST with thick border */}
                          <th 
                            className="border border-gray-300 px-2 py-1 text-center text-xs bg-yellow-100" 
                            style={{ 
                              minWidth: '70px',
                              borderRight: '3px solid #666' 
                            }}
                          >
                            Delta
                          </th>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Object.values(groupedRecords).map((group, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10" style={{ minWidth: '100px', maxWidth: '100px', width: '100px' }}>
                    {group.customer_name}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 sticky left-[100px] bg-white z-10" style={{ minWidth: '120px', maxWidth: '120px', width: '120px' }}>
                    {group.part_number}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 sticky left-[220px] bg-white z-10" style={{ minWidth: '200px', maxWidth: '200px', width: '200px' }}>
                    {group.model_name}
                  </td>
                  {Object.keys(forecastData).map((month, monthIdx) => {
                    const record = group.months[month] || {};
                    const isCurrent = isCurrentMonth(month);
                    const isPast = isPastMonth(month);
                    const showExtendedColumns = isCurrent || !isPast;
                    
                    return (
                      <React.Fragment key={monthIdx}>
                        <td className="border border-gray-300 px-2 py-1 text-right text-sm" style={{ minWidth: '80px', maxWidth: '80px', width: '80px' }}>
                          {record.unit_price ? `$${Number(record.unit_price).toFixed(2)}` : '-'}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right text-sm" style={{ minWidth: '60px', maxWidth: '60px', width: '60px' }}>
                          {record.qty || '-'}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right text-sm" style={{ minWidth: '80px', maxWidth: '80px', width: '80px' }}>
                          {record.amt ? `$${Number(record.amt).toFixed(2)}` : '-'}
                        </td>
                        {showExtendedColumns && (
                          <>
                            <td 
                              className="border border-gray-300 px-2 py-1 text-right bg-red-50"
                              style={{ fontSize: '11px', minWidth: '80px' }}
                            >
                              {isEditing(group.customer_name, group.part_number, month, 'firm_po_qty') ? (
                                <input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => {
                                    console.log('📝 Input onChange:', e.target.value);
                                    setEditValue(e.target.value);
                                    editValueRef.current = e.target.value; // ✅ ADD THIS LINE
                                  }}
                                  onBlur={() => {
                                    console.log('👋 Input onBlur triggered');
                                    saveEdit();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      console.log('⏎ Enter pressed');
                                      saveEdit();
                                    }
                                    if (e.key === 'Escape') {
                                      console.log('⎋ Escape pressed');
                                      cancelEdit();
                                    }
                                  }}
                                  autoFocus
                                  onFocus={(e) => e.target.select()}
                                  className="w-full px-1 text-right border border-blue-500 focus:outline-none"
                                  style={{ fontSize: '11px' }}
                                />
                              ) : (
                                <span
                                  onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'firm_po_qty', record.firm_po_qty)}
                                  className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
                                  title="Double-click to edit"
                                >
                                  {record.firm_po_qty ? (typeof record.firm_po_qty === 'string' ? parseFloat(record.firm_po_qty) : record.firm_po_qty) : 0}
                                </span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'firm_po_amt') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'firm_po_amt', record.firm_po_amt)} className="cursor-pointer block w-full h-full px-1">{record.firm_po_amt || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '90px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'firm_po_risk_qty') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'firm_po_risk_qty', record.firm_po_risk_qty)} className="cursor-pointer block w-full h-full px-1">{record.firm_po_risk_qty || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '90px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'firm_po_risk_amt') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'firm_po_risk_amt', record.firm_po_risk_amt)} className="cursor-pointer block w-full h-full px-1">{record.firm_po_risk_amt || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'fc_vmi_low_risk_qty') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'fc_vmi_low_risk_qty', record.fc_vmi_low_risk_qty)} className="cursor-pointer block w-full h-full px-1">{record.fc_vmi_low_risk_qty || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'fc_vmi_low_risk_amt') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'fc_vmi_low_risk_amt', record.fc_vmi_low_risk_amt)} className="cursor-pointer block w-full h-full px-1">{record.fc_vmi_low_risk_amt || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'fc_vmi_high_risk_qty') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'fc_vmi_high_risk_qty', record.fc_vmi_high_risk_qty)} className="cursor-pointer block w-full h-full px-1">{record.fc_vmi_high_risk_qty || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'fc_vmi_high_risk_amt') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'fc_vmi_high_risk_amt', record.fc_vmi_high_risk_amt)} className="cursor-pointer block w-full h-full px-1">{record.fc_vmi_high_risk_amt || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '100px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'markup_qty_historical') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'markup_qty_historical', record.markup_qty_historical)} className="cursor-pointer block w-full h-full px-1">{record.markup_qty_historical || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '100px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'markup_qty_historical_amt') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'markup_qty_historical_amt', record.markup_qty_historical_amt)} className="cursor-pointer block w-full h-full px-1">{record.markup_qty_historical_amt || 0}</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '11px', minWidth: '80px' }}>
                              {isEditing(group.customer_name, group.part_number, month, 'delta') ? (
                                <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} autoFocus onFocus={(e) => e.target.select()} className="w-full px-1 text-right" style={{ fontSize: '11px' }} />
                              ) : (
                                <span onDoubleClick={() => startEdit(group.customer_name, group.part_number, month, 'delta', record.delta)} className="cursor-pointer block w-full h-full px-1">{record.delta || 0}</span>
                              )}
                            </td>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const fetchBudgetData = async () => {
    if (budgetQuarters.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setBudgetLoading(true);
    try {
      const payload = {
        year: budgetYear,
        quarters: budgetQuarters.join(',')
      };

      const response = await service.getSalesBudgetGrouped(payload);
      console.log('Budget Data:', response);
      
      if (response.data.success) {
        setBudgetData(response.data.data || {});
        toast.success('Budget data fetched successfully!');
      } else {
        setBudgetData({});
        toast.info('No budget data found');
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
      toast.error('Failed to fetch budget data');
      setBudgetData({});
    } finally {
      setBudgetLoading(false);
    }
  };

  const renderBudgetTable = (data) => {
    if (budgetLoading) {
      return (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-600">Loading...</p>
        </div>
      );
    }

    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          No data available. Please select filters and click Search.
        </div>
      );
    }

    const groupedRecords = {};
    Object.entries(data).forEach(([month, records]) => {
      records.forEach(record => {
        const key = `${record.customer_name}_${record.part_number}`;
        if (!groupedRecords[key]) {
          groupedRecords[key] = {
            customer_name: record.customer_name,
            part_number: record.part_number,
            model_name: record.model_name,
            months: {}
          };
        }
        groupedRecords[key].months[month] = record;
      });
    });

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthNum = currentDate.getMonth() + 1; // 1-12
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonthName = monthNames[currentMonthNum - 1];
    
    // Financial year: Apr (4) to Mar (3 of next year)
    let financialYear;
    if (currentMonthNum >= 4) {
      // Apr-Dec: use current year
      financialYear = currentYear;
    } else {
      // Jan-Mar: use previous year (since financial year started last April)
      financialYear = currentYear - 1;
    }
    
    // Current month in "Apr 2026" format to match backend
    const currentMonth = `${currentMonthName} ${currentYear}`;

    const isCurrentMonth = (month) => {
      // month format from backend: "Apr 2026"
      return month === currentMonth;
    };
    
    const isPastMonth = (month) => {
      // Parse "Apr 2026" format
      const [monthName, yearStr] = month.split(' ');
      const monthYear = new Date(parseInt(yearStr), monthNames.indexOf(monthName));
      const current = new Date(currentYear, currentMonthNum - 1);
      return monthYear < current;
    };
    
    const isFutureMonth = (month) => {
      const [monthName, yearStr] = month.split(' ');
      const monthYear = new Date(parseInt(yearStr), monthNames.indexOf(monthName));
      const current = new Date(currentYear, currentMonthNum - 1);
      return monthYear > current;
    };

    const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
      const cellKey = `${customerName}_${partNumber}_${month}_${field}`;
      const isCurrentlyEditing = editingCell === cellKey;
      
      const handleSave = () => {
        const numericValue = parseFloat(editValue);
        
        // Allow saving 0 and any valid number
        if (isNaN(numericValue)) {
          setEditingCell(null);
          setEditValue('');
          return;
        }

        // Store the pending change for budget
        setBudgetPendingChanges(prev => ({
          ...prev,
          [cellKey]: {
            customerName,
            partNumber,
            month,
            field,
            value: numericValue
          }
        }));

        // Update local display
        setBudgetData(prevData => {
          const newData = { ...prevData };
          const monthRecords = newData[month];
          if (monthRecords) {
            const recordIndex = monthRecords.findIndex(
              r => r.customer_name === customerName && r.part_number === partNumber
            );
            if (recordIndex > -1) {
              monthRecords[recordIndex] = {
                ...monthRecords[recordIndex],
                [field]: numericValue
              };
            }
          }
          return newData;
        });

        setBudgetHasUnsavedChanges(true);
        setEditingCell(null);
        setEditValue('');
      };

      const handleCancel = () => {
        setEditingCell(null);
        setEditValue('');
      };
      
      return (
        <td className={`border border-gray-300 px-2 py-1 text-right ${bgColor}`} style={{ fontSize: '11px', minWidth: '80px' }}>
          {isCurrentlyEditing ? (
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
                if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              autoFocus
              className="w-full px-1 border border-blue-500 focus:outline-none"
              style={{ fontSize: '11px' }}
            />
          ) : (
            <span
              onDoubleClick={() => {
                setEditingCell(cellKey);
                setEditValue(value || 0);
              }}
              className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
              title="Double-click to edit"
            >
              {value || 0}
            </span>
          )}
        </td>
      );
    };

    return (
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '13px' }}>
          <thead className="sticky top-0 z-20">
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-2 py-2 sticky left-0 bg-gray-100 z-30" style={{ minWidth: '150px', fontSize: '13px' }}>
                Customer Name
              </th>
              <th className="border border-gray-300 px-2 py-2 sticky left-[150px] bg-gray-100 z-30" style={{ minWidth: '120px', fontSize: '13px' }}>
                Part Number
              </th>
              <th className="border border-gray-300 px-2 py-2 sticky left-[270px] bg-gray-100 z-30" style={{ minWidth: '150px', fontSize: '13px' }}>
                Model Name
              </th>
              {Object.keys(data).sort().map((month, idx) => {
                const isPast = isPastMonth(month);
                const colSpan = isPast ? 3 : 9;

                return (
                  <th key={idx} className="border border-gray-300 px-2 py-2 bg-blue-50" colSpan={colSpan}>
                    <div className="text-center font-semibold" style={{ fontSize: '13px' }}>{month}</div>
                  </th>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 sticky left-0 bg-gray-50 z-30"></th>
              <th className="border border-gray-300 sticky left-[150px] bg-gray-50 z-30"></th>
              <th className="border border-gray-300 sticky left-[270px] bg-gray-50 z-30"></th>
              {Object.keys(data).sort().map((month, idx) => {
                const isPast = isPastMonth(month);

                return (
                  <React.Fragment key={idx}>
                    <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Unit Price</th>
                    <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Qty</th>
                    <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Amount</th>
                    {!isPast && (
                      <>
                        <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>CF Qty</th>
                        <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>CF Amt</th>
                        <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>Pot Qty</th>
                        <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>Pot Amt</th>
                        <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>Markup Qty</th>
                        <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>Markup Amt</th>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Object.values(groupedRecords).map((group, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10" style={{ minWidth: '150px', fontSize: '13px' }}>
                  {group.customer_name}
                </td>
                <td className="border border-gray-300 px-2 py-1 sticky left-[150px] bg-white z-10" style={{ minWidth: '120px', fontSize: '13px' }}>
                  {group.part_number}
                </td>
                <td className="border border-gray-300 px-2 py-1 sticky left-[270px] bg-white z-10" style={{ minWidth: '150px', fontSize: '13px' }}>
                  {group.model_name}
                </td>
                {Object.keys(data).sort().map((month, monthIdx) => {
                  const record = group.months[month] || {};
                  const isPast = isPastMonth(month);

                  return (
                    <React.Fragment key={monthIdx}>
                      {isPast ? (
                        <>
                          <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                            {record.unit_price || 0}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                            {record.qty || 0}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                            {record.amt || 0}
                          </td>
                        </>
                      ) : (
                        <>
                          {renderEditableCell(group.customer_name, group.part_number, month, 'unit_price', record.unit_price)}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'qty', record.qty)}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'amt', record.amt)}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_qty', record.current_forecast_qty, 'bg-blue-50')}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_amt', record.current_forecast_amt, 'bg-blue-50')}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'potential_qty', record.potential_qty, 'bg-green-50')}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'potential_amt', record.potential_amt, 'bg-green-50')}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'markup_qty', record.markup_qty, 'bg-yellow-50')}
                          {renderEditableCell(group.customer_name, group.part_number, month, 'markup_amt', record.markup_amt, 'bg-yellow-50')}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleBudgetSaveAll = async () => {
    if (Object.keys(budgetPendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      // Group changes by record
      const recordUpdates = {};
      
      Object.values(budgetPendingChanges).forEach(change => {
        const recordKey = `${change.customerName}_${change.partNumber}_${change.month}`;
        if (!recordUpdates[recordKey]) {
          recordUpdates[recordKey] = {
            customerName: change.customerName,
            partNumber: change.partNumber,
            month: change.month,
            fields: {}
          };
        }
        recordUpdates[recordKey].fields[change.field] = change.value;
      });

      // Save each record using budget/update API
      for (const update of Object.values(recordUpdates)) {
        const payload = {
          customer_name: update.customerName,
          part_number: update.partNumber,
          month: update.month,
          ...update.fields
        };

        await service.updateSalesBudget(payload);
      }

      toast.success("All budget changes saved successfully!");
      setBudgetHasUnsavedChanges(false);
      setBudgetPendingChanges({});
      fetchBudgetData(); // Refresh data
    } catch (error) {
      console.error("Error saving budget changes:", error);
      toast.error(error.response?.data?.message || "Error saving budget changes");
    }
  };

  const handleBudgetCancelAll = () => {
    setBudgetPendingChanges({});
    setBudgetHasUnsavedChanges(false);
    fetchBudgetData(); // Reload original data
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#1D1D1D]">Sales Total</h2>
        <button 
          className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer"
          onClick={() => navigate('/upload-sales-total')}
        >
          <FaUpload className="text-white" />
          <span className="text-white">Import</span>
        </button>
      </div>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Forecast" key="1">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={selectedQuarters}
                  onChange={(values) => setSelectedQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchSalesTotalData}
                  disabled={loading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{loading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>

              {/* ✅ Forecast Save Buttons */}
              {hasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {renderDataTable(data)}
        </TabPane>

        <TabPane tab="Budget" key="2">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={budgetYear}
                  onChange={(e) => setBudgetYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={budgetQuarters}
                  onChange={(values) => setBudgetQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchBudgetData}
                  disabled={budgetLoading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{budgetLoading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>
              
              {/* ✅ ADD SAVE BUTTONS HERE */}
              {budgetHasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleBudgetSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleBudgetCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {renderBudgetTable(budgetData)}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SalesTotal;









